import { useEffect, useState } from 'react'
import { platformService } from '@/services/platformService'

type Event = { id: number; ts: string; actor: string; action: string; target: string; ip: string }
type Summary = { loginSuccess: number; loginFailure: number; mfaEnabledEvents: number; chainValid: boolean }

const ACTION_STYLE = (a: string) =>
    a.includes('FAILURE') ? 'text-rose-400'
        : a.includes('SUCCESS') || a.includes('ENABLED') ? 'text-emerald-400'
            : a.includes('SUSPEND') || a.includes('DISABLE') ? 'text-amber-400'
                : 'text-slate-300'

export default function PlatformAuditPage() {
    const [events, setEvents] = useState<Event[]>([])
    const [chainValid, setChainValid] = useState<boolean | null>(null)
    const [summary, setSummary] = useState<Summary | null>(null)
    const [loading, setLoading] = useState(true)

    const load = () => {
        setLoading(true)
        Promise.all([platformService.auditEvents(), platformService.securitySummary()])
            .then(([a, s]) => { setEvents(a.events); setChainValid(a.chainValid); setSummary(s) })
            .catch(() => { /* noop */ })
            .finally(() => setLoading(false))
    }
    useEffect(load, [])

    const cards = summary ? [
        { label: 'Logins OK', value: summary.loginSuccess, cls: 'text-emerald-400' },
        { label: 'Logins fallidos', value: summary.loginFailure, cls: 'text-rose-400' },
        { label: 'MFA activados', value: summary.mfaEnabledEvents, cls: 'text-indigo-400' },
    ] : []

    return (
        <div>
            <div className="mb-6 flex items-center justify-between">
                <h1 className="text-2xl font-semibold">Auditoría y Seguridad</h1>
                <div className="flex items-center gap-3">
                    {chainValid !== null && (
                        <span className={`rounded-full px-3 py-1 text-sm ${chainValid ? 'bg-emerald-900 text-emerald-300' : 'bg-rose-900 text-rose-300'}`}>
                            {chainValid ? '🔒 Cadena íntegra' : '⚠️ Cadena alterada'}
                        </span>
                    )}
                    <button onClick={load} className="rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800">Refrescar</button>
                </div>
            </div>

            <div className="mb-6 grid grid-cols-3 gap-4">
                {cards.map((c) => (
                    <div key={c.label} className="rounded-xl border border-slate-800 bg-slate-800/50 p-5">
                        <div className={`text-3xl font-semibold ${c.cls}`}>{c.value}</div>
                        <div className="mt-1 text-sm text-slate-400">{c.label}</div>
                    </div>
                ))}
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-800">
                <table className="w-full text-left text-sm">
                    <thead className="bg-slate-800/70 text-slate-300">
                        <tr>
                            <th className="px-4 py-3">Fecha</th>
                            <th className="px-4 py-3">Actor</th>
                            <th className="px-4 py-3">Acción</th>
                            <th className="px-4 py-3">Objetivo</th>
                            <th className="px-4 py-3">IP</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                        {loading && events.length === 0 ? (
                            <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">Cargando…</td></tr>
                        ) : events.length === 0 ? (
                            <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">Sin eventos</td></tr>
                        ) : events.map((e) => (
                            <tr key={e.id} className="text-slate-200">
                                <td className="px-4 py-2 font-mono text-xs text-slate-400">{e.ts.replace('T', ' ').slice(0, 19)}</td>
                                <td className="px-4 py-2">{e.actor || '—'}</td>
                                <td className={`px-4 py-2 font-mono text-xs ${ACTION_STYLE(e.action)}`}>{e.action}</td>
                                <td className="px-4 py-2 text-slate-400">{e.target || '—'}</td>
                                <td className="px-4 py-2 font-mono text-xs text-slate-500">{e.ip || '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}
